// @flow
/* global ProgressEvent */
import type { Config, AjaxHttpResponse } from 'app/packages/api/types';

const noop = (...args) => undefined;

export default function xhr({
  async,
  data,
  headers,
  timeout,
  type: method,
  updateProgress,
  url,
}: Config): {| promise: Promise<AjaxHttpResponse>, abort: () => void |} {
  const xhr = new XMLHttpRequest();

  const promise = new Promise((resolve, reject) => {
    // Build timeout callback
    const abortTimeout = timeout
      ? setTimeout(() => {
          xhr.onreadystatechange = noop;
          xhr.abort();
          reject({ type: 'timeout' });
        }, timeout)
      : undefined;

    // Attach state change listener
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        xhr.onreadystatechange = noop;
        clearTimeout(abortTimeout);
        const status = xhr.status;
        const statusText = xhr.statusText;
        const bodyString = xhr.responseText;

        // The HTTP status will be 0 if there's a connectivity error
        // or if the request was cancelled due to a navigation.
        if (!status) {
          reject({ type: 'abort' });
          return;
        }

        resolve({
          status,
          statusText,
          ok: (status >= 200 && status < 300) || status === 304,
          headers: {
            get: (headerName: string) => xhr.getResponseHeader(headerName),
            _logString: () => xhr.getAllResponseHeaders(),
          },
          json: () =>
            new Promise((resolve, reject) => {
              try {
                const json = JSON.parse(bodyString);
                if (json instanceof Object) {
                  resolve(json);
                } else {
                  reject(new Error('JSON.parse did not return an object'));
                }
              } catch (e) {
                reject(e);
              }
            }),
          responseText: bodyString,
        });
      }
    };

    if (updateProgress) {
      xhr.upload.addEventListener('progress', (e: ProgressEvent) => {
        let percentComplete = null;
        if (e.lengthComputable) {
          percentComplete = (e.loaded / e.total) * 100;
        }
        updateProgress(percentComplete);
      });
    }

    // Open the request
    xhr.open(method, url, async);

    // Append all the required headers
    Object.keys(headers).forEach((headerName) => {
      xhr.setRequestHeader(headerName, headers[headerName]);
    });

    // Fire off the request
    xhr.send(data || null);
  });

  return {
    promise,
    abort: () => {
      if (xhr) {
        xhr.abort();
      }
    },
  };
}
